// §7.5 — version history, at kernel level so every app gets it free (D-005).
//
// Two halves. `thinVersions` is the retention rule and is pure: a list, a clock, a policy, and
// the exact set that survives. `createVersionStore` is the storage around it — a mirror of the
// live path under `/System/.versions`, with the bytes content-addressed by SHA-256 (D-047) so
// fifty saves of a document that changed one line still cost fifty blobs only where they differ.
//
// **Restore is non-destructive.** It goes back out through the ordinary write path, so restoring
// appends a version of its own and is itself undoable.
import {
  joinVfs,
  thinVersions as thinVersionsRule,
  type FileEntry,
  type RetentionPolicy,
  type ThinResult,
  type VersionEntry,
  type VfsPath,
} from '@hangar/shared';
import { nanoid } from 'nanoid';
import {
  VERSION_HOURLY_DAYS,
  VERSION_KEEP_ALL_HOURS,
  VERSION_KEEP_COUNT,
  VERSION_KEEP_DAYS,
} from '@/config';
import { SYSTEM_VERSIONS, type VfsInternal } from './tree';

export const DEFAULT_RETENTION: RetentionPolicy = {
  keepCount: VERSION_KEEP_COUNT,
  keepDays: VERSION_KEEP_DAYS,
  keepAllHours: VERSION_KEEP_ALL_HOURS,
  hourlyDays: VERSION_HOURLY_DAYS,
};

export type { RetentionPolicy, ThinResult };

/**
 * §7.5's retention rule, with this package's policy as the default.
 *
 * The rule itself lives in `shared/` because the server thins `file_versions` by exactly the same
 * one and two implementations would eventually disagree about somebody's history (D-155). What
 * stays here is the *policy*, which §22 keeps in each package's `config.ts`.
 */
export function thinVersions(
  entries: readonly VersionEntry[],
  now: number,
  policy: RetentionPolicy = DEFAULT_RETENTION,
): ThinResult {
  return thinVersionsRule(entries, now, policy);
}

// ── the store ──────────────────────────────────────────────────────────────────────────────

export interface VersionStore {
  /** Append a version holding the bytes just written to `path`, then thin. */
  record(path: VfsPath, bytes: Uint8Array): Promise<VersionEntry>;
  /** Newest first. Empty when nothing has ever been written to `path`. */
  list(path: VfsPath): Promise<VersionEntry[]>;
  read(path: VfsPath, versionId: string): Promise<Uint8Array>;
  /** Writes the old bytes back through the live write path, so this appends a version too. */
  restore(path: VfsPath, versionId: string): Promise<FileEntry>;
  /** History follows the file — otherwise a later file at the old path inherits it. */
  rename(from: VfsPath, to: VfsPath): Promise<void>;
  /** Drop a path's history entirely. */
  forget(path: VfsPath): Promise<void>;
}

export interface VersionStoreOptions {
  /** The `/System`-writable view (§7.5's store lives under `/System/.versions`). */
  files: VfsInternal;
  /** The public write, so a restore is an ordinary write and picks up its own version. */
  writeLive: (path: VfsPath, bytes: Uint8Array) => Promise<FileEntry>;
  now?: () => number;
  policy?: RetentionPolicy;
}

export class VersionNotFoundError extends Error {
  readonly code = 'NOT_FOUND' as const;
  readonly path: string;
  readonly versionId: string;

  constructor(path: string, versionId: string) {
    super(`versions: no version ${JSON.stringify(versionId)} for ${JSON.stringify(path)}`);
    this.name = 'VersionNotFoundError';
    this.path = path;
    this.versionId = versionId;
  }
}

const INDEX_FILE = 'index.json';

/**
 * The history for `/Users/guest/Documents/a.txt` lives at
 * `/System/.versions/Users/guest/Documents/a.txt/`. Mirroring rather than hashing keeps the
 * store readable in a debugger, and cannot collide: a path is either a file or a directory in
 * the live tree, never both.
 */
export function versionDir(path: VfsPath): VfsPath {
  return `${SYSTEM_VERSIONS}${path}`;
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

interface IndexFile {
  version: 1;
  entries: VersionEntry[];
}

function isVersionEntry(value: unknown): value is VersionEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === 'string' &&
    typeof entry.path === 'string' &&
    typeof entry.rev === 'number' &&
    typeof entry.size === 'number' &&
    typeof entry.createdAt === 'number' &&
    typeof entry.sha === 'string'
  );
}

export function createVersionStore(options: VersionStoreOptions): VersionStore {
  const { files, writeLive } = options;
  const now = options.now ?? (() => Date.now());
  const policy = options.policy ?? DEFAULT_RETENTION;

  const blobPath = (dir: VfsPath, sha: string): VfsPath => joinVfs(dir, `${sha}.bin`);

  async function readIndex(dir: VfsPath): Promise<VersionEntry[]> {
    let raw: string;
    try {
      raw = new TextDecoder().decode(await files.read(joinVfs(dir, INDEX_FILE)));
    } catch {
      return []; // no history yet, or the index is gone — either way, start clean
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return [];
      const entries = (parsed as { entries?: unknown }).entries;
      if (!Array.isArray(entries)) return [];
      return entries.filter(isVersionEntry);
    } catch {
      // A corrupt index must not make the file unopenable; the bytes are still on disk.
      return [];
    }
  }

  async function writeIndex(dir: VfsPath, entries: readonly VersionEntry[]): Promise<void> {
    const payload: IndexFile = { version: 1, entries: [...entries] };
    await files.write(joinVfs(dir, INDEX_FILE), JSON.stringify(payload));
  }

  return {
    async record(path, bytes) {
      const dir = versionDir(path);
      const sha = await sha256Hex(bytes);
      const entry: VersionEntry = {
        id: nanoid(12),
        path,
        // §7.4's rev is assigned by the server; a local version has never been synced.
        rev: 0,
        size: bytes.byteLength,
        createdAt: now(),
        sha,
      };

      // Content addressing (D-047): identical bytes are stored once, however many versions
      // point at them.
      if (!(await files.exists(blobPath(dir, sha)))) {
        await files.write(blobPath(dir, sha), bytes);
      }

      const { keep, drop } = thinVersions([entry, ...(await readIndex(dir))], now(), policy);
      await writeIndex(dir, keep);

      for (const gone of drop) {
        if (keep.some((kept) => kept.sha === gone.sha)) continue;
        try {
          await files.remove(blobPath(dir, gone.sha));
        } catch {
          // A blob that is already gone is the outcome we wanted.
        }
      }
      return entry;
    },

    async list(path) {
      return readIndex(versionDir(path));
    },

    async read(path, versionId) {
      const dir = versionDir(path);
      const entry = (await readIndex(dir)).find((candidate) => candidate.id === versionId);
      if (!entry) throw new VersionNotFoundError(path, versionId);
      return files.read(blobPath(dir, entry.sha));
    },

    async restore(path, versionId) {
      const dir = versionDir(path);
      const entry = (await readIndex(dir)).find((candidate) => candidate.id === versionId);
      if (!entry) throw new VersionNotFoundError(path, versionId);
      const bytes = await files.read(blobPath(dir, entry.sha));
      // Straight back out through the live write: that is what appends the new version and
      // makes the restore itself undoable (§7.5).
      return writeLive(path, bytes);
    },

    async rename(from, to) {
      const source = versionDir(from);
      if (!(await files.exists(source))) return;
      await files.move(source, versionDir(to));
    },

    async forget(path) {
      const dir = versionDir(path);
      if (!(await files.exists(dir))) return;
      await files.remove(dir, { recursive: true });
    },
  };
}
